import type { CacheConfig, Variables } from 'relay-runtime';
import isEqual from 'lodash/isEqual';

import { isDataFetcherAllFinished } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { SSR_TEST_QUERY_FIRED } from '@confluence/graphql/entry-points/SSRTestLink.ts';
import { getQueryBoundary } from '@confluence/query-preloader-boundary/entry-points/queryBoundaryStorage.ts';
import { expVal } from '@confluence/feature-experiments';

// Keep query listeners at loading state when GLOBAL_APOLLO_SKIP_QUERY is true.
// SSR runtime will cancel all the in-flight requests once the main function exits.
// Outside the feature-gated path, we still allow queries that were already in-flight, and preloads during streaming.

const inFlightQueries = new Map<string, Array<Variables> | undefined>();

const clearInFlightQueries = () => {
	inFlightQueries.clear();
};

const isQueryInFlightWithVariables = (operationName: string, vars: Variables) =>
	Boolean(inFlightQueries.get(operationName)?.some((v) => isEqual(v, vars)));

const isPreload = (metadata: CacheConfig['metadata']): boolean => {
	// TODO: Migrate usage of `preload` from Apollo context to Relay's cacheConfig.metadata.
	// See Jira: CCE2EPERF-834 (created from CCE2EPERF-659 PR discussion)
	return Boolean(window.__SSR_REACT_STREAMING__ && metadata?.preload);
};

export const trackInFlightIfAllowed = ({
	operationName,
	variables,
}: {
	operationName: string;
	variables: Variables;
}) => {
	// TODO: Create GLOBAL_RELAY_SKIP_QUERY env var
	if (window['GLOBAL_APOLLO_SKIP_QUERY']) {
		return;
	}
	if (isQueryInFlightWithVariables(operationName, variables)) {
		return;
	}
	inFlightQueries.set(operationName, (inFlightQueries.get(operationName) || []).concat(variables));
};

const logBlockedQuery = (operationName: string, reason: string) => {
	if (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging' ||
		process.env.CLOUD_ENV === 'hello'
	) {
		// eslint-disable-next-line no-console
		console.warn(`Query blocked: ${operationName}. Reason: ${reason}`);
	}
};

export const shouldBlockRequest = ({
	operationName,
	variables,
	metadata,
	operationKind,
}: {
	operationName: string;
	variables: Variables;
	metadata: CacheConfig['metadata'];
	operationKind?: string;
}): boolean => {
	if (!window['GLOBAL_APOLLO_SKIP_QUERY']) {
		return false;
	}

	// experimental is not being used for Relay

	if (operationKind === 'subscription') {
		logBlockedQuery(operationName, 'Subscription');
		return true;
	}

	if (expVal('block_network_requests_from_rendering', 'isEnabled', false)) {
		// Allow the query only if it is fired within a boundary (preloader).
		if (getQueryBoundary()) {
			return false;
		}

		logBlockedQuery(
			operationName,
			'No query boundary. Query fired outside preloaders after rendering started.',
		);
		return true;
	} else {
		if (
			isQueryInFlightWithVariables(operationName, variables) ||
			isPreload(metadata) ||
			!isDataFetcherAllFinished()
		) {
			return false;
		}

		logBlockedQuery(operationName, 'Query fired after all data fetchers finished.');
		return true;
	}
};

export const markSSRTestQueryFired = (operationName: string) => {
	SSR_TEST_QUERY_FIRED[operationName] = (SSR_TEST_QUERY_FIRED[operationName] || 0) + 1;
};

export const __TEST_ONLY__ = {
	_inFlightQueries: inFlightQueries,
	clearInFlightQueries,
};
